import { scrollPosition, useOnNavigation, useStaticData } from '@avensia/excite';
import { Position, Flyout } from '@avensia/excite-ui';
import { TestIdProps } from '@avensia/test-automation';
import { styled } from '@glitz/react';
import * as React from 'react';
import { Block, desktopEdgeMarginDecorator, maxContentNormalWidthDecorator } from 'webapp/components/page-layout';
import { bareListDecorator } from 'webapp/decorators/list';
import { addNavigationEventToDataLayer } from 'webapp/tracking/events/main-navigation';
import { ZIndex } from 'webapp/z-index';
import { isKitchentime } from 'webapp/theme';
import { useDesktopMenuState } from 'webapp/shared/use-desktop-menu-state';
import { DesktopMenu } from 'webapp/shell/main-menu/DesktopMenu';
import Link from 'webapp/components/Link';
import {
  BANNER_DESKTOP_HEIGHT,
  CENTER_TOP_MENU,
  DESKTOP_HEADER_HEIGHT,
  DESKTOP_HEADER_HEIGHT_WITH_BANNER,
} from 'webapp/utils/constants';

const TopMenu = React.forwardRef<HTMLDivElement>((props, ref) => {
  const menu = useStaticData<NordicNest.Api.CfMenuApiModel>();
  const [selectedCategory, setSelectedCategory] = React.useState<NordicNest.Api.MenuItemApiModel>(
    {} as NordicNest.Api.MenuItemApiModel,
  );
  const { open, close: closeMenu, menuState: isOpen } = useDesktopMenuState();
  const [activeLevel, setActiveLevel] = React.useState<number>(2);

  const resetMenu = React.useCallback(() => {
    setActiveLevel(2);
  }, []);

  const close = React.useCallback(() => {
    closeMenu();
    resetMenu();
  }, [closeMenu, resetMenu]);

  useOnNavigation(close);

  const positionTopDecorator = styled({
    top: __BROWSER__
      ? scrollPosition() <= BANNER_DESKTOP_HEIGHT
        ? DESKTOP_HEADER_HEIGHT_WITH_BANNER - scrollPosition()
        : DESKTOP_HEADER_HEIGHT
      : undefined,
  });

  return (
    <Base ref={ref}>
      <Content>
        <Categories>
          {menu?.primaryLinks.map((menuItem: NordicNest.Api.MenuItemApiModel, i) => (
            <li key={i}>
              <TopMenuLink
                onClick={e => {
                  if (menuItem.childPages.length > 0) {
                    e.preventDefault();
                    setSelectedCategory(menuItem);
                    resetMenu();
                    open();
                  } else {
                    close();
                  }
                }}
                to={menuItem.link.to}
                {...TestIdProps.ProductCategoryTabs}
              >
                {menuItem.link.children}
              </TopMenuLink>
            </li>
          ))}
        </Categories>
        <MenuFlyout
          onClose={close}
          position={Position.Left}
          visible={isOpen}
          zIndex={ZIndex.Header}
          overlayDecorator={positionTopDecorator}
          css={positionTopDecorator}
        >
          {menu && (
            <DesktopMenu
              menu={menu}
              close={close}
              selectedCategory={selectedCategory}
              activeLevel={activeLevel}
              setActiveLevel={setActiveLevel}
            />
          )}
        </MenuFlyout>
        {!CENTER_TOP_MENU && (
          <Pages>
            {menu?.primaryTopLinks.map((menuItem, i) => (
              <li key={i}>
                <SecondaryTopMenuLink
                  to={menuItem.link.to}
                  onClick={() =>
                    addNavigationEventToDataLayer({ ...menuItem, menu_type: 'header', menu_category: 'top_menu' })
                  }
                >
                  {menuItem.link.children}
                </SecondaryTopMenuLink>
              </li>
            ))}
          </Pages>
        )}
      </Content>
    </Base>
  );
});

const Base = styled.nav({
  padding: { top: t => t.sharedSpacing.xs, bottom: t => t.sharedSpacing.m },
  width: '100%',
  minHeight: 44,
});

const Content = styled(Block, maxContentNormalWidthDecorator, desktopEdgeMarginDecorator, {
  display: 'flex',
  justifyContent: CENTER_TOP_MENU ? 'center' : 'space-between',
});

const NavigationList = styled.ul(bareListDecorator, {
  display: 'flex',
  gap: t => t.sharedSpacing.l,
});

const Categories = styled(NavigationList);
const Pages = styled(NavigationList);

/** @glitz-dynamic */
const TopMenuLink = styled(Link, {
  fontSize: t => t.sharedFontSize.xs,
  fontWeight: t => t.header?.topMenu.fontWeight,
  display: 'block',
  whiteSpace: 'nowrap',
  ':hover': {
    textDecoration: 'underline',
  },
});

const SecondaryTopMenuLink = styled(TopMenuLink, {
  fontWeight: t => (isKitchentime ? t.fontWeight?.normal : t.header?.fontWeight),
});

const MenuFlyout = styled(Flyout, {
  width: 'auto',
});

export default TopMenu;
